import { useEffect, useRef, useState } from 'react';
import {
  DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors,
  type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../../../utils/cn';
import {
  Button, ErrorState, Input, PageHeader, PageLoader,
  Switch, Tabs, Textarea, useToast,
} from '../../../components/ui';
import { FormSection } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { siteContentService } from '../services/websiteService';
import type { ContactInfo, ServiceAreaRegion, ServiceItem, SiteContent, SiteSections, StatItem, StepItem } from '../types/websiteTypes';

type Tab = 'contact-info' | 'visibility' | 'hero' | 'stats' | 'services' | 'how' | 'why' | 'areas';

const TABS: { value: Tab; label: string }[] = [
  { value: 'contact-info',  label: 'Contact Info' },
  { value: 'visibility',    label: 'Sections' },
  { value: 'hero',          label: 'Hero' },
  { value: 'stats',         label: 'Stats' },
  { value: 'services',      label: 'Services' },
  { value: 'how',           label: 'How It Works' },
  { value: 'why',           label: 'Why Us' },
  { value: 'areas',         label: 'Service Areas' },
];

/* ── Stable ID generator ────────────────────────────────────────── */

let _dndCtr = 0;
const mkId = () => `dnd-${++_dndCtr}`;

/* ── Shared drag-list hook ──────────────────────────────────────── */

function useDragList<T>(items: T[], onReorder: (next: T[]) => void) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const idsRef = useRef<string[]>(items.map(mkId));
  while (idsRef.current.length < items.length) idsRef.current.push(mkId());
  const ids = idsRef.current.slice(0, items.length);

  const [activeId, setActiveId] = useState<string | null>(null);
  const activeIdx = activeId ? ids.indexOf(activeId) : -1;

  const onDragStart = ({ active }: DragStartEvent) => setActiveId(String(active.id));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to   = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    idsRef.current = arrayMove(ids, from, to);
    onReorder(arrayMove([...items], from, to));
  };

  const onDragCancel = () => setActiveId(null);

  const remove = (i: number) => {
    idsRef.current = idsRef.current.filter((_, j) => j !== i);
    onReorder(items.filter((_, j) => j !== i));
  };

  const add = (item: T) => {
    idsRef.current = [...ids, mkId()];
    onReorder([...items, item]);
  };

  return { sensors, ids, activeId, activeIdx, onDragStart, onDragEnd, onDragCancel, remove, add };
}

/* ── Drag handle ────────────────────────────────────────────────── */

function GripIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
      <circle cx="4.5" cy="2.5"  r="1.4" /><circle cx="9.5" cy="2.5"  r="1.4" />
      <circle cx="4.5" cy="7"    r="1.4" /><circle cx="9.5" cy="7"    r="1.4" />
      <circle cx="4.5" cy="11.5" r="1.4" /><circle cx="9.5" cy="11.5" r="1.4" />
    </svg>
  );
}

function DragHandle({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      title="Drag to reorder"
      className={cn(
        'flex-shrink-0 cursor-grab touch-none rounded-lg p-2 text-ink-300',
        'hover:bg-ink-50 hover:text-ink-500 active:cursor-grabbing',
        className,
      )}
      {...props}
    >
      <GripIcon />
    </button>
  );
}

/* ── Sortable item wrapper ──────────────────────────────────────── */

function SortableItem({
  id, isDragging: isActive, children,
}: {
  id: string;
  isDragging: boolean;
  children: (h: React.ButtonHTMLAttributes<HTMLButtonElement>) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'rounded-xl transition-shadow',
        isDragging && 'z-10 shadow-lg ring-2 ring-brand-300',
        isActive && !isDragging && 'opacity-30',
      )}
    >
      {children({ ...attributes, ...listeners } as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    </div>
  );
}

/* ── Reusable row editors ───────────────────────────────────────── */

type DragProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

function StatRow({ item, onChange, onRemove, dragProps }: {
  item: StatItem; onChange: (v: StatItem) => void; onRemove: () => void;
  dragProps?: DragProps;
}) {
  return (
    <div className="flex items-stretch gap-2 rounded-xl border border-line bg-white p-3">
      <DragHandle {...dragProps} className="self-center" />
      <div className="grid flex-1 grid-cols-[80px_70px_1fr] items-start gap-3">
        <Input label="Value" type="number" value={item.value}
          onChange={(e) => onChange({ ...item, value: Number(e.target.value) })} />
        <Input label="Suffix" value={item.suffix}
          onChange={(e) => onChange({ ...item, suffix: e.target.value })} placeholder="+" />
        <Input label="Label" value={item.label}
          onChange={(e) => onChange({ ...item, label: e.target.value })} placeholder="Projects Completed" />
      </div>
      <button type="button" onClick={onRemove}
        className="self-start mt-6 rounded-lg p-1.5 text-ink-300 hover:bg-red-50 hover:text-red-500" title="Remove">
        ✕
      </button>
    </div>
  );
}

function ServiceRow({ item, onChange, onRemove, dragProps }: {
  item: ServiceItem; onChange: (v: ServiceItem) => void; onRemove: () => void;
  dragProps?: DragProps;
}) {
  return (
    <div className="flex gap-2 rounded-xl border border-line bg-white p-4">
      <DragHandle {...dragProps} className="self-start mt-1" />
      <div className="flex-1">
        <div className="mb-3 grid grid-cols-2 gap-3">
          <Input label="Title" value={item.title}
            onChange={(e) => onChange({ ...item, title: e.target.value })} />
          <Input label="Icon name" value={item.icon}
            onChange={(e) => onChange({ ...item, icon: e.target.value })} placeholder="bolt" />
          <Input label="Color key" value={item.colorKey}
            onChange={(e) => onChange({ ...item, colorKey: e.target.value })} placeholder="brand" />
        </div>
        <Textarea label="Description" value={item.desc}
          onChange={(e) => onChange({ ...item, desc: e.target.value })} rows={2} />
        <button type="button" onClick={onRemove}
          className="mt-3 text-[12px] text-red-600 hover:underline">Remove service</button>
      </div>
    </div>
  );
}

function StepRow({ item, onChange, onRemove, label, dragProps }: {
  item: StepItem; onChange: (v: StepItem) => void; onRemove: () => void; label: string;
  dragProps?: DragProps;
}) {
  return (
    <div className="flex gap-2 rounded-xl border border-line bg-white p-4">
      <DragHandle {...dragProps} className="self-start mt-1" />
      <div className="flex-1">
        <div className="mb-3 grid grid-cols-2 gap-3">
          <Input label="Title" value={item.title}
            onChange={(e) => onChange({ ...item, title: e.target.value })} />
          <Input label="Icon name" value={item.icon}
            onChange={(e) => onChange({ ...item, icon: e.target.value })} placeholder="checkCircle" />
        </div>
        <Textarea label="Description" value={item.desc}
          onChange={(e) => onChange({ ...item, desc: e.target.value })} rows={2} />
        <button type="button" onClick={onRemove}
          className="mt-3 text-[12px] text-red-600 hover:underline">Remove {label}</button>
      </div>
    </div>
  );
}

function AreasInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState(value.join(', '));
  const committed = useRef(value.join(','));

  useEffect(() => {
    const next = value.join(',');
    if (next !== committed.current) {
      committed.current = next;
      setText(value.join(', '));
    }
  }, [value]);

  return (
    <Input
      label="Towns / areas (comma-separated)"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const parsed = text.split(',').map((s) => s.trim()).filter(Boolean);
        committed.current = parsed.join(',');
        onChange(parsed);
      }}
    />
  );
}

/* ── Per-tab DnD sections ───────────────────────────────────────── */

type UpdArr = <T>(key: keyof SiteContent, items: T[]) => void;

function StatsTab({ items, updArr }: { items: StatItem[]; updArr: UpdArr }) {
  const { sensors, ids, activeId, activeIdx, onDragStart, onDragEnd, onDragCancel, remove, add } =
    useDragList(items, (next) => updArr('stats', next));
  return (
    <FormSection title="Stats bar" subtitle="Numbers shown in the strip below the hero. Drag the ⠿ handle to reorder." bodyClassName="!grid-cols-1">
      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {items.map((s, i) => (
              <SortableItem key={ids[i]} id={ids[i]} isDragging={ids[i] === activeId}>
                {(h) => (
                  <StatRow item={s} dragProps={h}
                    onChange={(v) => updArr('stats', items.map((x, j) => j === i ? v : x))}
                    onRemove={() => remove(i)} />
                )}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeIdx >= 0 && (
            <div className="rotate-1 shadow-2xl">
              <StatRow item={items[activeIdx]} onRemove={() => {}} onChange={() => {}} />
            </div>
          )}
        </DragOverlay>
      </DndContext>
      <button type="button" onClick={() => add({ value: 0, suffix: '+', label: '' })}
        className="mt-4 text-[13px] font-medium text-brand-600 hover:underline">+ Add stat</button>
    </FormSection>
  );
}

function ServicesTab({ items, updArr }: { items: ServiceItem[]; updArr: UpdArr }) {
  const { sensors, ids, activeId, activeIdx, onDragStart, onDragEnd, onDragCancel, remove, add } =
    useDragList(items, (next) => updArr('services', next));
  return (
    <FormSection title="Services" subtitle="The service cards shown on the homepage. Drag the ⠿ handle to reorder." bodyClassName="!grid-cols-1">
      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {items.map((s, i) => (
              <SortableItem key={ids[i]} id={ids[i]} isDragging={ids[i] === activeId}>
                {(h) => (
                  <ServiceRow item={s} dragProps={h}
                    onChange={(v) => updArr('services', items.map((x, j) => j === i ? v : x))}
                    onRemove={() => remove(i)} />
                )}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeIdx >= 0 && (
            <div className="rotate-1 shadow-2xl">
              <ServiceRow item={items[activeIdx]} onRemove={() => {}} onChange={() => {}} />
            </div>
          )}
        </DragOverlay>
      </DndContext>
      <button type="button" onClick={() => add({ icon: 'bolt', colorKey: 'brand', title: '', desc: '' })}
        className="mt-4 text-[13px] font-medium text-brand-600 hover:underline">+ Add service</button>
    </FormSection>
  );
}

function HowTab({ items, updArr }: { items: StepItem[]; updArr: UpdArr }) {
  const { sensors, ids, activeId, activeIdx, onDragStart, onDragEnd, onDragCancel, remove, add } =
    useDragList(items, (next) => updArr('howItWorks', next));
  return (
    <FormSection title="How it works" subtitle="The step-by-step process section. Drag the ⠿ handle to reorder." bodyClassName="!grid-cols-1">
      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {items.map((s, i) => (
              <SortableItem key={ids[i]} id={ids[i]} isDragging={ids[i] === activeId}>
                {(h) => (
                  <StepRow item={s} label="step" dragProps={h}
                    onChange={(v) => updArr('howItWorks', items.map((x, j) => j === i ? v : x))}
                    onRemove={() => remove(i)} />
                )}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeIdx >= 0 && (
            <div className="rotate-1 shadow-2xl">
              <StepRow item={items[activeIdx]} label="step" onRemove={() => {}} onChange={() => {}} />
            </div>
          )}
        </DragOverlay>
      </DndContext>
      <button type="button" onClick={() => add({ icon: 'checkCircle', title: '', desc: '' })}
        className="mt-4 text-[13px] font-medium text-brand-600 hover:underline">+ Add step</button>
    </FormSection>
  );
}

function WhyTab({ items, updArr }: { items: StepItem[]; updArr: UpdArr }) {
  const { sensors, ids, activeId, activeIdx, onDragStart, onDragEnd, onDragCancel, remove, add } =
    useDragList(items, (next) => updArr('whyUs', next));
  return (
    <FormSection title="Why us" subtitle="The trust-building cards. Drag the ⠿ handle to reorder." bodyClassName="!grid-cols-1">
      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {items.map((s, i) => (
              <SortableItem key={ids[i]} id={ids[i]} isDragging={ids[i] === activeId}>
                {(h) => (
                  <StepRow item={s} label="card" dragProps={h}
                    onChange={(v) => updArr('whyUs', items.map((x, j) => j === i ? v : x))}
                    onRemove={() => remove(i)} />
                )}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeIdx >= 0 && (
            <div className="rotate-1 shadow-2xl">
              <StepRow item={items[activeIdx]} label="card" onRemove={() => {}} onChange={() => {}} />
            </div>
          )}
        </DragOverlay>
      </DndContext>
      <button type="button" onClick={() => add({ icon: 'shield', title: '', desc: '' })}
        className="mt-4 text-[13px] font-medium text-brand-600 hover:underline">+ Add card</button>
    </FormSection>
  );
}

function AreaCard({ r, i, items, updArr, dragProps }: {
  r: ServiceAreaRegion; i: number; items: ServiceAreaRegion[]; updArr: UpdArr;
  dragProps?: DragProps;
}) {
  return (
    <div className="flex gap-2 rounded-xl border border-line bg-white p-4">
      <DragHandle {...dragProps} className="self-start mt-1" />
      <div className="flex-1 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Region name" value={r.region}
            onChange={(e) => updArr('serviceAreas', items.map((x, j) =>
              j === i ? { ...x, region: e.target.value } : x))} />
          <div className="flex items-end pb-1">
            <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-ink-700">
              <input type="checkbox" checked={!!r.primary}
                className="h-4 w-4 rounded border-line accent-brand-600"
                onChange={(e) => updArr('serviceAreas', items.map((x, j) =>
                  j === i ? { ...x, primary: e.target.checked } : x))} />
              Primary area
            </label>
          </div>
        </div>
        <AreasInput
          value={r.areas}
          onChange={(areas) => updArr('serviceAreas', items.map((x, j) =>
            j === i ? { ...x, areas } : x))} />
        <button type="button"
          onClick={() => updArr('serviceAreas', items.filter((_, j) => j !== i))}
          className="text-[12px] text-red-600 hover:underline">Remove region</button>
      </div>
    </div>
  );
}

function AreasTab({ items, updArr }: { items: ServiceAreaRegion[]; updArr: UpdArr }) {
  const { sensors, ids, activeId, activeIdx, onDragStart, onDragEnd, onDragCancel, remove, add } =
    useDragList(items, (next) => updArr('serviceAreas', next));
  return (
    <FormSection title="Service areas" subtitle="Regions and towns where you offer services. Drag the ⠿ handle to reorder." bodyClassName="!grid-cols-1">
      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {items.map((r, i) => (
              <SortableItem key={ids[i]} id={ids[i]} isDragging={ids[i] === activeId}>
                {(h) => (
                  <AreaCard r={r} i={i} items={items} updArr={updArr} dragProps={h} />
                )}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeIdx >= 0 && (
            <div className="rotate-1 shadow-2xl">
              <AreaCard r={items[activeIdx]} i={activeIdx} items={items} updArr={() => {}} />
            </div>
          )}
        </DragOverlay>
      </DndContext>
      <button type="button" onClick={() => add({ region: '', areas: [], primary: false })}
        className="mt-4 text-[13px] font-medium text-brand-600 hover:underline">+ Add region</button>
    </FormSection>
  );
}

/* ── Main page ──────────────────────────────────────────────────── */

export default function WebsiteSettings() {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('contact-info');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<SiteContent | null>(null);

  const { data, loading, error, reload } = useFetch(() => siteContentService.get(), []);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (loading || !form) return <PageLoader />;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await siteContentService.update(form);
      setForm(updated);
      toast.success('Website content saved');
    } catch (e: any) {
      toast.error(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setContact = (key: keyof ContactInfo, value: string) =>
    setForm((f) => f ? { ...f, contactInfo: { ...f.contactInfo, [key]: value } } : f);

  const setSection = (key: keyof SiteSections, value: boolean) =>
    setForm((f) => f ? { ...f, sections: { ...f.sections, [key]: value } } : f);

  const setHero = (key: keyof SiteContent['hero'], value: string | string[]) =>
    setForm((f) => f ? { ...f, hero: { ...f.hero, [key]: value } } : f);

  const updArr: UpdArr = <T,>(key: keyof SiteContent, items: T[]) =>
    setForm((f) => f ? { ...f, [key]: items } : f);

  return (
    <div>
      <PageHeader
        title="Website content"
        subtitle="Edit the customer-facing website: hero, services, stats and more."
        actions={<Button onClick={save} loading={saving}>Save changes</Button>}
      />

      <Tabs<Tab> tabs={TABS} value={tab} onChange={setTab} className="mb-6" />

      {/* ── Contact Info ── */}
      {tab === 'contact-info' && (
        <FormSection title="Contact information" subtitle="Phone, email and address shown across the website and in the navbar." bodyClassName="!grid-cols-1">
          <div className="space-y-4">
            <Input label="Phone number" value={form.contactInfo.phone} onChange={(e) => setContact('phone', e.target.value)} placeholder="+91 98765 43210" />
            <Input label="Email address" type="email" value={form.contactInfo.email} onChange={(e) => setContact('email', e.target.value)} placeholder="info@example.com" />
            <Input label="Office / site address" value={form.contactInfo.address} onChange={(e) => setContact('address', e.target.value)} placeholder="123 Main Road, City" />
            <Input label="Service area" value={form.contactInfo.serviceArea} onChange={(e) => setContact('serviceArea', e.target.value)} placeholder="Your City & surrounding areas" />
            <Textarea label="Emergency call-to-action text" value={form.contactInfo.emergencyText} onChange={(e) => setContact('emergencyText', e.target.value)} rows={2} />
          </div>
        </FormSection>
      )}

      {/* ── Visibility ── */}
      {tab === 'visibility' && (
        <FormSection title="Section visibility" subtitle="Toggle which sections are shown on the customer website. The Hero is always visible." bodyClassName="!grid-cols-1">
          <div className="space-y-2">
            {(
              [
                { key: 'stats',        label: 'Stats bar',     desc: 'Numbers strip below the hero (500+ projects, 24/7, etc.)' },
                { key: 'services',     label: 'Services',      desc: 'Service cards section' },
                { key: 'howItWorks',   label: 'How It Works',  desc: 'Step-by-step process section' },
                { key: 'whyUs',        label: 'Why Us',        desc: 'Trust-building cards section' },
                { key: 'serviceAreas', label: 'Service Areas', desc: 'Where We Serve map section' },
                { key: 'contact',      label: 'Contact form',  desc: 'Get a Free Quote form section' },
              ] as { key: keyof SiteSections; label: string; desc: string }[]
            ).map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between rounded-xl border border-line p-4">
                <div>
                  <p className="text-[14px] font-medium text-navy-900">{label}</p>
                  <p className="text-[12px] text-ink-400">{desc}</p>
                </div>
                <Switch checked={form.sections[key]} onChange={(checked) => setSection(key, checked)} />
              </div>
            ))}
          </div>
        </FormSection>
      )}

      {/* ── Hero ── */}
      {tab === 'hero' && (
        <FormSection title="Hero section" subtitle="The first thing visitors see at the top of the page." bodyClassName="!grid-cols-1">
          <div className="space-y-4">
            <Input label="Badge text" value={form.hero.badge} onChange={(e) => setHero('badge', e.target.value)} placeholder="Licensed & Insured Electricians" />
            <Input label="Headline" value={form.hero.headline} onChange={(e) => setHero('headline', e.target.value)} />
            <Textarea label="Subheadline" value={form.hero.subheadline} onChange={(e) => setHero('subheadline', e.target.value)} rows={3} />
            <Input label="CTA button label" value={form.hero.ctaLabel} onChange={(e) => setHero('ctaLabel', e.target.value)} />
            <div>
              <Input label="Hero image URL" value={form.hero.imageUrl} onChange={(e) => setHero('imageUrl', e.target.value)} placeholder="https://… or leave blank to use the default illustration" />
              {form.hero.imageUrl && (
                <img src={form.hero.imageUrl} alt="Hero preview" className="mt-3 max-h-48 rounded-xl border border-line object-cover" onError={(e) => (e.currentTarget.style.display = 'none')} />
              )}
            </div>
            <div>
              <p className="mb-2 text-[13px] font-medium text-ink-600">Trust points</p>
              {form.hero.trustPoints.map((pt, i) => (
                <div key={i} className="mb-2 flex gap-2">
                  <Input value={pt} onChange={(e) => {
                    const pts = [...form.hero.trustPoints];
                    pts[i] = e.target.value;
                    setHero('trustPoints', pts);
                  }} placeholder={`Point ${i + 1}`} />
                  <button type="button" onClick={() => {
                    const pts = form.hero.trustPoints.filter((_, j) => j !== i);
                    setHero('trustPoints', pts);
                  }} className="rounded p-2 text-ink-400 hover:bg-red-50 hover:text-red-600">✕</button>
                </div>
              ))}
              <button type="button" onClick={() => setHero('trustPoints', [...form.hero.trustPoints, ''])}
                className="text-[13px] font-medium text-brand-600 hover:underline">+ Add point</button>
            </div>
          </div>
        </FormSection>
      )}

      {tab === 'stats'    && <StatsTab    items={form.stats}        updArr={updArr} />}
      {tab === 'services' && <ServicesTab items={form.services}     updArr={updArr} />}
      {tab === 'how'      && <HowTab      items={form.howItWorks}   updArr={updArr} />}
      {tab === 'why'      && <WhyTab      items={form.whyUs}        updArr={updArr} />}
      {tab === 'areas'    && <AreasTab    items={form.serviceAreas} updArr={updArr} />}

    </div>
  );
}
